<template>
    <div class="quickpay-esign-overview">
        <QuickpayPaymentHeader
            :companyName="data.corporation.name"
            :amount="data.document.amount_total"
            :currency="data.document.currency"
            :logoUrl="data.corporation.logoUrl"
        />
        <QuickpayDownloadDocument
            :docType="data.document.doctype"
            :pdfUrl="data.document.pdf_link"
            :documentNumber="data.document.number"
            :documentStep="data.document.step"
            :expired="data.document.expired"
            class="quickpay-payment-overview__download-document"
        />
        <QuickpayCGV
            v-if="data.condition"
            :condition="data.condition"
            :checked="cgvAccepted"
            :hasError="cgvError"
            @update:checked="handleCgvChange"
        />
        <div class="quickpay-payment-overview__cards">
            <template v-if="loading">
                <div
                    v-for="index in 3"
                    :key="`skeleton-${index}`"
                    :class="[
                        'quickpay-payment-overview__skeleton-card',
                        { 'quickpay-payment-overview__skeleton-card--first': index === 1 },
                    ]"
                >
                    <CompassSkeleton variant="line" size="large" />
                </div>
            </template>
            <template v-else-if="hasError || availablePaymentMethods.length === 0">
                <CompassAlert
                    type="error"
                    :description="$t.__('quickpay.QuickpayPaymentOverview.payment_methods_error')"
                    :showIcon="false"
                />
            </template>
            <template v-else>
                <div v-for="paymentMethod in availablePaymentMethods" :key="paymentMethod.type">
                    <QuickpayPaymentCard
                        :type="paymentMethod.type"
                        :isSelected="selectedPaymentMethod === paymentMethod.type"
                        :data="paymentMethod.data"
                        @select="handleSelectPaymentMethod"
                        @pay="handlePaymentClick"
                    />
                    <form
                        v-if="paymentMethod.type === 'paypal'"
                        ref="paypalForm"
                        :action="paymentMethod.data.form_action"
                        method="post"
                        style="display: none"
                    >
                        <input type="hidden" name="cmd" value="_express-checkout" />
                        <input type="hidden" name="token" :value="paymentMethod.data.token" />
                    </form>
                    <form
                        v-else-if="paymentMethod.type === 'atos'"
                        ref="atosForm"
                        :action="paymentMethod.data.form_action"
                        method="post"
                        style="display: none"
                    >
                        <input type="hidden" name="Data" :value="paymentMethod.data.data" />
                        <input type="hidden" name="InterfaceVersion" value="HP_2.9" />
                        <input type="hidden" name="Seal" :value="paymentMethod.data.seal" />
                    </form>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import QuickpayPaymentHeader from 'vcomponents/quickpay/QuickpayPaymentHeader.vue';
import QuickpayCGV from 'vcomponents/quickpay/QuickpayCGV.vue';
import QuickpayPaymentCard from 'vcomponents/quickpay/payment/QuickpayPaymentCard.vue';
import QuickpayDownloadDocument from 'vcomponents/quickpay/QuickpayDownloadDocument.vue';

const PAYMENT_METHODS = ['stripe', 'gocardless', 'adyenPayByLink', 'atos', 'paypal'];

export default {
    components: {
        QuickpayPaymentCard,
        QuickpayPaymentHeader,
        QuickpayCGV,
        QuickpayDownloadDocument,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        hash: {
            type: String,
            required: true,
        },
        deadlineId: {
            type: Number,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            cgvAccepted: false,
            cgvError: false,
            selectedPaymentMethod: null,
            availablePaymentMethods: [],
            loading: true,
            hasError: false,
        };
    },
    computed: {
        hasCgv() {
            return !!this.data.condition;
        },
    },
    created() {
        this.fetchPaymentMethods();
    },
    methods: {
        fetchPaymentMethods() {
            this.$ajax
                .post(
                    '/quickpay/payment-methods',
                    {},
                    { hash: this.hash, deadlineId: this.deadlineId },
                )
                .then((response) => {
                    this.availablePaymentMethods = PAYMENT_METHODS.filter(
                        (type) => response.data[type] !== undefined,
                    ).map(function (type) {
                        return { type: type, data: response.data[type] };
                    });
                    if (this.availablePaymentMethods.length > 0) {
                        this.selectedPaymentMethod = this.availablePaymentMethods[0].type;
                    }
                })
                .catch(() => {
                    this.hasError = true;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleCgvChange(value) {
            this.cgvAccepted = value;
            if (value) {
                this.cgvError = false;
            }
        },
        handleSelectPaymentMethod(paymentType) {
            this.selectedPaymentMethod = paymentType;
            this.cgvError = false;
        },
        handlePaymentClick(paymentType, paymentData) {
            if (this.hasCgv && !this.cgvAccepted) {
                this.cgvError = true;
                return;
            }

            if (!this.data.payments) {
                throw new Error('Payment method not available');
            }

            switch (paymentType) {
                case 'paypal':
                    this.$refs.paypalForm[0].submit();
                    break;
                case 'atos':
                    this.$refs.atosForm[0].submit();
                    break;
                case 'stripe':
                    this.$emit('show-stripe-payment');
                    break;
                default:
                    if (paymentData.link) {
                        window.location.href = paymentData.link;
                    } else {
                        throw new Error('Payment method not available');
                    }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-esign-overview {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: var(--spacing-3);
}

.quickpay-payment-overview {
    &__download-document {
        @media (min-width: 1024px) {
            display: none;
        }
    }

    &__cards {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
        width: 100%;
    }

    &__skeleton-card {
        max-width: 400px;
        width: 100%;

        :deep(.compass-skeleton__line) {
            width: 100%;
            height: 52px;
        }

        &--first {
            :deep(.compass-skeleton__line) {
                height: 140px;
            }
        }
    }
}
</style>
